import { React, useState, useEffect } from "react";
import axios from 'axios'
import { useParams, useNavigate } from 'react-router-dom';

import { Container, Form } from "react-bootstrap";
import { validateAndFocusForm } from '../../../utils/surveyValidationUtils';
import { useRealTimeValidation } from '../../../utils/realTimeValidationUtils';
import VAS from "../../../components/SelfAssessment/VAS/VAS";
import ASESElbow from '../../../components/SelfAssessment/ASESElbow/ASESElbow';
import ASESShoulder from '../../../components/SelfAssessment/ASESShoulder/ASESShoulder';
import SANE from "../../../components/SelfAssessment/SANE/SANE";
import VR12 from '../../../components/SelfAssessment/VR12/VR12';
import StandardLatePostOp from "../../../components/SelfAssessment/StandardLatePostOp/StandardLatePostOp";
import FormCompletion from '../../../components/SelfAssessment/FormCompletion/FormCompletion';
import CustomProgressBar from '../../../components/CustomProgressBar/CustomProgressBar';
import SurveyAlreadyCompleted from "../../../components/SelfAssessment/SurveyAlreadyCompleted/SurveyAlreadyCompleted";
import { BASE_URL } from "../../../utils/constants";
// import Final from "./Components/Forms/Final";

import './AfterXYears.scss'
import '../../../styles/survey-design-system.scss';
import '../../../styles/survey-validation.scss';

const AfterXYears = () => {
  const { eventId, customPatientId, year } = useParams();
  const treatmentSide = useParams()["treatmentSide"] === "left"? "Links" : "Rechts"
  const study = useParams()["study"].replace("-", " ")
  const navigate = useNavigate();
  useEffect(() => {
    if (!["elbow", "shoulder arthroscopy", "shoulder arthroplasty"].includes(study) || ((study === "elbow" || study === "shoulder arthroscopy") && !["1", "2", "5"].includes(year)) || ((study === "shoulder arthroplasty") && !(0 < parseInt(year) <= 15))) {
      navigate("/404")
    }
  });
  const numberOfSteps = 5;
  const [step, setStep] = useState(1);
  const [surveyCompleted, setSurveCompleted] = useState(false)
  const [isCheckingCompletion, setIsCheckingCompletion] = useState(true)
  const [validated, setValidated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  
  const [formData, setFormData] = useState({
    vasScore: "",
    saneScore: "",
    ases: {
      asesQuestion1: "",
      asesQuestion2: "",
      asesQuestion3: "",
      asesQuestion4: "",
      asesQuestion5: "",
      asesQuestion6: "",
      asesQuestion7: "",
      asesQuestion8: "",
      asesQuestion9: "",
      asesQuestion10: "",
      asesQuestion11: "",
      asesQuestion12: "",
      asesQuestion13: "",
      asesQuestion14: "",
      asesQuestion15: "",
      asesQuestion16: "",
      asesQuestion17: "",
      asesQuestion18: ""
    },
    vr12: {
      vr12Question1: "",
      vr12Question2: "",
      vr12Question3: "",
      vr12Question4: "",
      vr12Question5: "",
      vr12Question6: "",
      vr12Question7: "",
      vr12Question8: "",
      vr12Question9: "",
      vr12Question10: "",
      vr12Question11: "",
      vr12Question12: "",
      vr12Question13: "",
      vr12Question14: ""
    },
    slpostop: {
      slpostopQuestion1: "",
      slpostopQuestion2: "",
      slpostopQuestion3: "",
      slpostopQuestion4: ""
    }
  })

  const checkSurveyCompletion = async () => {
    try {
      const yearColumnValueMap = {
        "1": "one_year",
        "2": "two_years",
        "3": "three_years",
        "4": "four_years",
        "5": "five_years",
        "6": "six_years",
        "7": "seven_years",
        "8": "eight_years",
        "9": "nine_years",
        "10": "ten_years",
        "11": "eleven_years",
        "12": "twelve_years",
        "13": "thirteen_years",
        "14": "fourteen_years",
        "15": "fifteen_years",
      }
      const response = await axios.get(`${BASE_URL}/api/v1/self-assessment/check-survey-completion/${customPatientId}/${eventId}/${yearColumnValueMap[year]}`)
      setSurveCompleted(response.data.completed)
    }
    catch (error) {
      console.log(error)
    }
    finally {
      setIsCheckingCompletion(false)
    }
  }

  useEffect(() => {
    checkSurveyCompletion()
  }, [customPatientId, eventId, year])


  // Initialize real-time validation
  const { validateFieldRealTime, clearAllFieldValidations } = useRealTimeValidation(
    formData, step, study, setFieldErrors, setTouchedFields
  );

  // function for going to next step by increasing step state by 1
  const nextStep = () => {
    // Clear previous validation states
    clearAllFieldValidations();
    setValidationErrors([]);

    // Enhanced validation with auto-focus
    const validationResult = validateAndFocusForm(formData, step, study, {
      clearPreviousErrors: true,
      showVisualFeedback: true,
      scrollToFirst: true
    });

    setValidated(true);

    if (validationResult.isValid) {
      setValidated(false);
      setStep(step + 1);
    } else {
      // Set validation errors for display
      setValidationErrors(validationResult.invalidFields);

      // Announce error to screen readers
      
    }
  };

  // function for going to previous step by decreasing step state by 1
  const prevStep = () => {
    clearAllFieldValidations();
    setValidationErrors([]);
    setStep(step - 1);
  };

  // handling form input data by taking onchange value and updating our previous form data state
  const handleInputData = (type, input, value) => {
    // Update form data with proper immutable state management
    let updatedFormData;
    switch(type) {
      case "ases":
        updatedFormData = {
          ...formData,
          ases: {
            ...formData.ases,
            [input]: value
          }
        };
        break;
      case "vr12":
        updatedFormData = {
          ...formData,
          vr12: {
            ...formData.vr12,
            [input]: value
          }
        };
        break;
      case "slpostop":
        updatedFormData = {
          ...formData,
          slpostop: {
            ...formData.slpostop,
            [input]: value
          }
        };
        break;
      default:
        updatedFormData = {
          ...formData,
          [input]: value
        };
    }

    setFormData(updatedFormData);

    // Perform immediate real-time validation with instant feedback
    const fieldPath = (type === 'vas' || type === 'sane') ? input : `${type}.${input}`;
    validateFieldRealTime(fieldPath, value);

    // If the form has already been marked as validated (e.g., "Next" was clicked),
    // re-run the full validation for the current step to update all error states.
    if (validated) {
      setTimeout(() => {
        const validationResult = validateAndFocusForm(
          updatedFormData, // Use the most up-to-date form data
          step,
          study,
          {
            clearPreviousErrors: true,
            showVisualFeedback: true,
            scrollToFirst: false, // Do not scroll while the user is actively typing
          }
        );
        // Update the list of validation errors for the summary display
        setValidationErrors(validationResult.invalidFields);
      }, 0); // Use setTimeout to ensure this runs after the state update
    }
  }

const handleSubmit = async (event) => {
  event.preventDefault();

  if (isSubmitting) return; // Prevent double submission

  // Clear previous validation states
  clearAllFieldValidations();
  setValidationErrors([]);

  // Enhanced validation with auto-focus
  const validationResult = validateAndFocusForm(formData, step, study, {
    clearPreviousErrors: true,
    showVisualFeedback: true,
    scrollToFirst: true
  });

  if (validationResult.isValid) {
    setIsSubmitting(true);

    try {
      await axios.post(`${BASE_URL}/api/v1/self-assessment/year/${year}`, {
        eventId: eventId,
        customPatientId: customPatientId,
        study: study,
        formAnswers: formData
      }, {headers:{"Content-Type" : "application/json"}})
      setStep(numberOfSteps + 1);
    }
    catch(error) {
      console.log(error.response.data)
      // switch to error screen
    }
    finally {
      setIsSubmitting(false);
    }
  }
  else {
    setValidated(true);
    setValidationErrors(validationResult.invalidFields);
    
  }
};


const renderFormContent = step => {
  switch (step){
    // case 1 to show VAS form and passing nextStep, prevStep, and handleInputData as handleFormData method as prop and also formData as value to the fprm
    case 1:
      return <VAS handleFormData={handleInputData} values={formData} fieldErrors={fieldErrors} touchedFields={touchedFields} />
    // case 2 to show SANE form passing nextStep, prevStep, and handleInputData as handleFormData method as prop and also formData as value to the fprm
    case 2:
      if(study === "elbow") {
        return <ASESElbow handleFormData={handleInputData} values={formData} fieldErrors={fieldErrors} touchedFields={touchedFields} />
      }
      else {
        return <ASESShoulder handleFormData={handleInputData} values={formData} fieldErrors={fieldErrors} touchedFields={touchedFields} />
      }
    case 3:
      return <SANE handleFormData={handleInputData} study={study} values={formData} fieldErrors={fieldErrors} touchedFields={touchedFields} />
    case 4:
      return <VR12 handleFormData={handleInputData} values={formData} fieldErrors={fieldErrors} touchedFields={touchedFields} />
    case 5:
      return <StandardLatePostOp handleFormData={handleInputData} values={formData} fieldErrors={fieldErrors} touchedFields={touchedFields} />
    case 6:
      return <FormCompletion />
    default:
      return <div className="after-x-years"></div>
  }
}

// javascript switch case to show different form in each step

  return (
    <div className="modern-survey-container">
    <Form validated={validated} onSubmit={handleSubmit}>
      <div className="modern-survey-card">
        <div className="modern-survey-header">
          <div className="patient-identifier">
            <span className="patient-id-label">Patiënt:</span>
            <span className="patient-id-value">{customPatientId}</span>
          </div>
          <h1 className="survey-title">
            {study === "elbow"? "Elleboog" : "Schouder"} ({treatmentSide})
          </h1>
          <p className="survey-subtitle">Zelfevaluatie Na {year} Jaar</p>
        </div>
        {isCheckingCompletion ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            Laden...
          </div>
        ) : !surveyCompleted?
          (
            <>
            <div className="modern-progress-container">
              <CustomProgressBar className="form-progress-bar" currentStep={step} numberOfSteps={numberOfSteps} />
            </div>
            <div className="modern-survey-body">
              {validationErrors.length > 0 && (
                <div className="validation-summary" role="alert">
                  <h3 className="summary-title">
                    Er zijn fouten gevonden
                  </h3>
                  <ul className="summary-list">
                    {validationErrors.map((error, index) => (
                      <li key={index}>
                        <button
                          type="button"
                          className="error-link"
                          onClick={() => {
                            const field = document.querySelector(error.selector);
                            if (field) {
                              field.scrollIntoView({ behavior: 'smooth', block: 'center' });
                              field.focus();
                            }
                          }}
                        >
                          {error.message}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <Container className="form-content">
                {
                  renderFormContent(step)
                }
              </Container>
            </div>
              {step < (numberOfSteps + 1) ? (
                <div className={`modern-survey-footer ${step === 1 ? 'single-button' : ''}`}>
                  {step !== 1 && (
                    <button
                      type="button"
                      className="modern-btn modern-btn-secondary"
                      onClick={prevStep}
                      disabled={isSubmitting}
                    >
                      Vorige Stap
                    </button>
                  )}
                  {step < numberOfSteps && (
                    <button
                      type="button"
                      className="modern-btn modern-btn-primary"
                      onClick={nextStep}
                      disabled={isSubmitting}
                    >
                      Volgende Stap
                    </button>
                  )}
                  {step === numberOfSteps && (
                    <button
                      type="submit"
                      className={`modern-btn modern-btn-primary ${isSubmitting ? 'modern-btn-loading' : ''}`}
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Bezig met indienen...' : 'Indienen'}
                    </button>
                  )}
                </div>
              ) : null}
            </>
          ) :
          (
            <SurveyAlreadyCompleted />
          )
        }
      </div>
    </Form>
    </div>
  )
}

export default AfterXYears
    